import { HttpClient } from "@angular/common/http";
import { Component, HostListener, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Curso, CursosService } from "src/app/services/curso.service";
import { PipeRunService } from "src/app/services/piperun.service";
import Swal from "sweetalert2";

@Component({
  selector: "app-curso-graduacao-list",
  templateUrl: "./curso-graduacao-list.component.html",
})
export class CursoGraduacaoListComponent implements OnInit {
  public cursos: any[] = [];
  public cursosPaginados: any[] = [];

  public cursoSelecionado: any = null;

  public paginaAtual: number = 1;
  public cursosPorPagina: number = 9;
  public totalPaginas: number = 1;
  public paginasVisiveis: number = 5;

  public termoBusca: string = "";
  public grausSelecionados: string[] = [];
  public modalidadesSelecionadas: string[] = [];

  public cursosFiltrados: any[] = [];

  public windowWidth: number = window.innerWidth;

  public leadForm!: FormGroup;

  public filtroSelecionado: string = "graduacao";

  constructor(
    private http: HttpClient,
    private pipeRunService: PipeRunService,
    private fb: FormBuilder
  ) {}

  async ngOnInit(): Promise<void> {
    this.buildLeadForm();

    try {
      const todosCursos = await this.http
        .get<any[]>("assets/cursos.json")
        .toPromise();

      this.cursos = todosCursos.filter(
        (curso) => curso.filterClass === this.filtroSelecionado
      );

      this.cursosFiltrados = [...this.cursos];
      this.atualizarCursosPaginados();
    } catch (error) {}
  }

  public selecionarCurso(curso: any) {
    this.cursoSelecionado = curso;
  }

  public buildLeadForm() {
    this.leadForm = this.fb.group({
      name: [null, Validators.required],
      email: [null, [Validators.required, Validators.email]],
      mobile: [null, Validators.required],
    });
  }

  public async onSubmit() {
    const emailControl = this.leadForm.get("email");

    if (emailControl?.invalid) {
      if (emailControl.errors?.["email"]) {
        Swal.fire({
          title: "Atenção",
          text: "Por favor, insira um e-mail válido.",
          icon: "warning",
          customClass: {
            confirmButton: "swal2-confirm-style",
          },
        });
        return;
      }
    }

    if (this.leadForm.invalid) {
      Swal.fire({
        title: "Atenção",
        text: "Por favor, preencha todos os campos.",
        icon: "warning",
        customClass: {
          confirmButton: "swal2-confirm-style",
        },
      });
      return;
    }

    const form = this.leadForm.value;

    const params = {
      name: form.name,
      email: form.email,
      mobile: form.mobile,
      curso: this.cursoSelecionado.nome,
    };

    this.pipeRunService.create(params).subscribe({
      next: () => {
        const urlBase = this.cursoSelecionado.link;

        const queryParams = new URLSearchParams({
          nome: params.name,
          email: params.email,
          celular: params.mobile,
        }).toString();

        const separator = urlBase.includes("?") ? "&" : "?";

        const urlFinal = `${urlBase}${separator}${queryParams}`;

        window.location.href = urlFinal;

        this.leadForm.reset();
      },
      error: () => {
        Swal.fire({
          title: "Erro",
          text: "Algo deu errado. Tente novamente ou entre em contato com o suporte",
          icon: "error",
          customClass: {
            confirmButton: "swal2-confirm-style",
          },
        });
      },
    });
  }

  @HostListener("window:resize")
  public onResize() {
    this.windowWidth = window.innerWidth;
    this.definirPaginasVisiveis();
  }

  public definirPaginasVisiveis() {
    this.paginasVisiveis = this.windowWidth <= 768 ? 3 : 5;
  }

  public alternarGrau(grau: string): void {
    const index = this.grausSelecionados.indexOf(grau);
    if (index > -1) {
      this.grausSelecionados.splice(index, 1);
    } else {
      this.grausSelecionados.push(grau);
    }

    this.filtrarCursos();
  }

  public alternarModalidade(modalidade: string): void {
    const index = this.modalidadesSelecionadas.indexOf(modalidade);
    if (index > -1) {
      this.modalidadesSelecionadas.splice(index, 1);
    } else {
      this.modalidadesSelecionadas.push(modalidade);
    }
    this.filtrarCursos();
  }

  public filtrarCursos(): void {
    this.cursosFiltrados = this.cursos.filter((curso) => {
      const nomeValido = curso.nome
        .toLowerCase()
        .includes(this.termoBusca.toLowerCase());

      const grauValido =
        this.grausSelecionados.length === 0 ||
        curso.tags?.some((tag: string) => this.grausSelecionados.includes(tag));

      const modalidadeValida =
        this.modalidadesSelecionadas.length === 0 ||
        this.modalidadesSelecionadas.includes(curso.modalidade);

      return nomeValido && grauValido && modalidadeValida;
    });

    this.paginaAtual = 1;
    this.atualizarCursosPaginados();
  }

  public atualizarCursosPaginados(): void {
    const inicio = (this.paginaAtual - 1) * this.cursosPorPagina;
    const fim = inicio + this.cursosPorPagina;
    this.cursosPaginados = this.cursosFiltrados.slice(inicio, fim);
    this.totalPaginas = Math.ceil(
      this.cursosFiltrados.length / this.cursosPorPagina
    );
  }

  public mudarPagina(pagina: number): void {
    if (pagina >= 1 && pagina <= this.totalPaginas) {
      this.paginaAtual = pagina;
      this.atualizarCursosPaginados();
    }
  }

  public proximaPagina(): void {
    if (this.paginaAtual < this.totalPaginas) {
      this.paginaAtual++;
      this.atualizarCursosPaginados();
    }
  }

  public paginaAnterior(): void {
    if (this.paginaAtual > 1) {
      this.paginaAtual--;
      this.atualizarCursosPaginados();
    }
  }

  public getPaginasParaExibir(): number[] {
    const paginas = [];
    const metade = Math.floor(this.paginasVisiveis / 2);
    let inicio = Math.max(1, this.paginaAtual - metade);
    let fim = Math.min(this.totalPaginas, inicio + this.paginasVisiveis - 1);

    if (fim - inicio + 1 < this.paginasVisiveis) {
      inicio = Math.max(1, fim - this.paginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    return paginas;
  }

  public getTotalPorTag(tag: string): number {
    return this.cursos.filter((curso) => curso.tags?.includes(tag)).length;
  }

  public getTotalPorModalidade(modalidade: string): number {
    return this.cursos.filter((curso) => curso.modalidade === modalidade)
      .length;
  }

  public limparFiltros(): void {
    this.termoBusca = "";
    this.grausSelecionados = [];
    this.modalidadesSelecionadas = [];
    this.cursosFiltrados = [...this.cursos];
    this.paginaAtual = 1;
    this.atualizarCursosPaginados();
  }

  public formatCurrency(value: number) {
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  }

  public onEmailInput(event: any) {
    const input = event.target;
    input.value = input.value.toLowerCase();
  }
}
